/**
 * useStandardDraft — local draft state for the Standard tab
 * For Media Devoted | Created By Ciro Autiero
 *
 * Wraps the live Voluum flow into a mutable draft. Exposes a typed updater so
 * callers can patch nested fields without manually copying the whole shape.
 * `dirty` is a structural compare: any change vs the last GET marks the draft
 * dirty.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  VoluumFlowResource,
  VoluumFlowUpdate,
} from '@trafficpulse/shared';

import { summarizeFlowDiff, type FlowDiffSummary } from '../utils/diff-flow';
import { validateFlow, type ValidationIssue } from '../utils/flow-validators';
import { hydrateFlowPathAiSplit } from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';
import { flowSnapshotForDirtyCompare } from './flow-dirty-snapshot';

export interface UseStandardDraftResult {
  draft: VoluumFlowUpdate | null;
  setDraft: (next: VoluumFlowUpdate) => void;
  patch: (mutator: (draft: VoluumFlowUpdate) => void) => void;
  reset: () => void;
  /** Force the draft to a freshly-fetched flow (used after Apply rewrites it). */
  reseed: (flow: VoluumFlowResource) => void;
  dirty: boolean;
  issues: ValidationIssue[];
  diff: FlowDiffSummary;
}

function toDraft(live: VoluumFlowResource | undefined): VoluumFlowUpdate | null {
  if (!live) return null;
  const d = JSON.parse(JSON.stringify(live)) as VoluumFlowUpdate;
  hydrateFlowPathAiSplit(d);
  return d;
}

export function useStandardDraft(
  live: VoluumFlowResource | undefined,
): UseStandardDraftResult {
  const [draft, setDraftState] = useState<VoluumFlowUpdate | null>(() =>
    toDraft(live),
  );
  const liveRef = useRef<VoluumFlowResource | undefined>(live);

  useEffect(() => {
    if (live && live !== liveRef.current) {
      liveRef.current = live;
      setDraftState(toDraft(live));
    }
  }, [live]);

  const setDraft = useCallback((next: VoluumFlowUpdate) => {
    setDraftState({ ...next });
  }, []);

  const patch = useCallback(
    (mutator: (d: VoluumFlowUpdate) => void) => {
      setDraftState((prev) => {
        if (!prev) return prev;
        const copy = JSON.parse(JSON.stringify(prev)) as VoluumFlowUpdate;
        mutator(copy);
        return copy;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setDraftState(toDraft(liveRef.current));
  }, []);

  // Explicitly re-seed from a known-fresh flow (post-Apply), bypassing the
  // reference-equality guard above that a refetch may not trip.
  const reseed = useCallback((flow: VoluumFlowResource) => {
    liveRef.current = flow;
    setDraftState(toDraft(flow));
  }, []);

  const liveSnapshot = useMemo(
    () =>
      live ? flowSnapshotForDirtyCompare(live as VoluumFlowUpdate) : null,
    [live],
  );

  const dirty = useMemo(() => {
    if (!draft || !liveSnapshot) return false;
    return flowSnapshotForDirtyCompare(draft) !== liveSnapshot;
  }, [draft, liveSnapshot]);

  const issues = useMemo(() => (draft ? validateFlow(draft) : []), [draft]);
  const diff = useMemo(
    () =>
      draft
        ? summarizeFlowDiff(live, draft)
        : { hasChanges: false, bullets: [] },
    [draft, live],
  );

  return { draft, setDraft, patch, reset, reseed, dirty, issues, diff };
}
